import type { ItemResponse, PriceTierResponse } from '@palaccount/contracts';

/**
 * SAL-01's multi-tier pricing (§5a switch 1), the quotation grid's half of
 * owner ruling R5. With `invoicing.quote_multi_tier_pricing` on, the single
 * Unit-price column becomes ONE COLUMN PER TIER THE COMPANY HAS DEFINED —
 * any number, the company's own names, the company's own order — and the
 * line total uses the tier that applies to this customer (the partner's
 * MD-01 price-tier field, else the company's default tier).
 *
 * The prototype captured the other tiers' prices and then multiplied by the
 * standard price anyway — "every total is wrong for a wholesale customer"
 * (the defect BR-SAL-01-05 names, observed as ERR-SAL-01-08's silent
 * non-update). Here the APPLICABLE tier's column IS the line's price: it
 * seeds from the item's stored price for that tier and edits in place, so
 * typing a wholesale price moves the total the way §14 expected it to.
 * The remaining tier columns are the item's stored prices, read-only — the
 * quotation shows the company's price levels; it stores the one it offered.
 */

/**
 * BR-SAL-01-05a: the grid "must draw one price column per defined tier, in
 * the company's own order, and must not assume a count of three anywhere in
 * its layout or its arithmetic" — the columns are whatever the tenant
 * defined, ordered by the company's own sortOrder with the alphabet only
 * breaking ties (MD-14's rule), and nothing downstream indexes them by a
 * fixed count. Inactive tiers draw no column: a retired band is not offered
 * on a new document (BR-MD-14-06 keeps stored records untouched; this is
 * about NEW pricing).
 */
export function tierColumns(tiers: readonly PriceTierResponse[]): PriceTierResponse[] {
  return tiers
    .filter((tier) => tier.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/**
 * BR-SAL-01-05: "the line total must use the tier that applies to this
 * customer" — of the rule's two offered shapes ("pick the tier per
 * customer, or add a tier selector per line") this build picks per
 * customer: the partner's own price-tier field (MD-01 field 26) names the
 * band, falling back to the company's default tier when the partner carries
 * none or carries a retired one.
 */
export function applicableTierId(
  partnerTierId: string | null | undefined,
  columns: readonly PriceTierResponse[],
): string | null {
  const partnerTier =
    partnerTierId == null ? undefined : columns.find((tier) => tier.id === partnerTierId);
  if (partnerTier !== undefined) return partnerTier.id;
  return columns.find((tier) => tier.isDefault)?.id ?? null;
}

/**
 * The price a tier column shows for an item: the stored per-tier price
 * (MD-03 panel 4), else the item's unit price — the default tier's column
 * "is the same stored value as Unit price — the two are one field under two
 * headings" (SAL-01 §5c L7), and the unit price is the documented fallback
 * for a band the item was never priced at (MD-05's "what exists instead").
 */
export function tierPriceFor(
  item: { prices: ReadonlyArray<Pick<ItemResponse['prices'][number], 'tierId' | 'price'>>; unitPrice: string },
  tierId: string | null,
): string {
  if (tierId !== null) {
    const stored = item.prices.find((price) => price.tierId === tierId);
    if (stored !== undefined) return stored.price;
  }
  return item.unitPrice;
}
